import { useState } from 'react';
import { Icon } from '@vibe/core';
import { Column, Close, TextCopy, LongText, Email, Link, Counter, Heart, Status, Calendar, Team, Timeline, Files } from '@vibe/icons';
import './CreateColumnModal.css';

/**
 * CreateColumnModal
 *
 * Reusable "Column Center" modal that lets the user create a new column
 * on a Monday board directly via the Monday SDK — no backend required.
 *
 * Props:
 *  - isOpen          {boolean}           Controls visibility
 *  - onClose         {function}          Called when the user dismisses the modal
 *  - onColumnCreated {function(column)}  Called with { id, title, type } after creation
 *  - boardId         {number|string}     Target board ID
 *  - allowedTypes    {string[]}          Only these column types are shown
 *  - monday          {object}            The monday-sdk-js instance (pre-configured)
 *  - fieldLabel      {string}            Label of the NPS field being mapped (for context heading)
 */

// Full catalog of Monday column types the app supports.
// Only entries whose `apiType` is in `allowedTypes` will be shown.
const COLUMN_TYPE_CATALOG = [
  {
    apiType: 'email',
    label: 'Email',
    description: 'Store email addresses for contacts',
    icon: Email,
  },
  {
    apiType: 'text',
    label: 'Text',
    description: 'Short text, addresses, or keywords',
    icon: TextCopy,
  },
  {
    apiType: 'long_text',
    label: 'Long Text',
    description: 'Multi-line text for detailed content',
    icon: LongText,
  },
  {
    apiType: 'numbers',
    label: 'Numbers',
    description: 'Revenue, scores, counts, and more',
    icon: Counter,
  },
  {
    apiType: 'rating',
    label: 'Rating',
    description: 'Star ratings from 1 to 5',
    icon: Heart,
  },
  {
    apiType: 'status',
    label: 'Status',
    description: 'Color-coded labels to track progress',
    icon: Status,
  },
  {
    apiType: 'link',
    label: 'Link',
    description: 'Store URLs and hyperlinks',
    icon: Link,
  },
  {
    apiType: 'date',
    label: 'Date',
    description: 'Deadlines, due dates, and milestones',
    icon: Calendar,
  },
  {
    apiType: 'people',
    label: 'People',
    description: 'Assign team members to items',
    icon: Team,
  },
  {
    apiType: 'timeline',
    label: 'Timeline',
    description: 'Track duration with start and end dates',
    icon: Timeline,
  },
  {
    apiType: 'file',
    label: 'Files',
    description: 'Store attachments, such as a generated PDF',
    icon: Files,
  },
];

// Map internal type aliases (used in MAPPING_CONFIG) to API enum types.
// e.g. 'numeric' in the config maps to 'numbers' in the API.
const TYPE_ALIAS_MAP = {
  numeric: 'numbers',
  'long-text': 'long_text',
  color: 'status',
};

const normalizeType = (t) => TYPE_ALIAS_MAP[t] || t;

const CreateColumnModal = ({
  isOpen,
  onClose,
  onColumnCreated,
  boardId,
  allowedTypes = [],
  monday,
  fieldLabel = '',
}) => {
  const [loadingType, setLoadingType] = useState(null);
  const [error, setError] = useState(null);
  // title input: keyed per type so each card has its own independent value
  const [titles, setTitles] = useState({});

  if (!isOpen) return null;

  // Normalize allowedTypes to API enum values, then filter catalog
  const normalizedAllowed = new Set(allowedTypes.map(normalizeType));
  const visibleTypes = COLUMN_TYPE_CATALOG.filter((ct) =>
    normalizedAllowed.has(ct.apiType)
  );

  const getTitle = (ct) =>
    titles[ct.apiType] !== undefined ? titles[ct.apiType] : ct.label;

  const handleTitleChange = (apiType, value) => {
    setTitles((prev) => ({ ...prev, [apiType]: value }));
  };

  const handleCreate = async (ct) => {
    const title = getTitle(ct).trim() || ct.label;
    setLoadingType(ct.apiType);
    setError(null);

    try {
      const res = await monday.api(`
        mutation {
          create_column(
            board_id: ${boardId},
            title: "${title.replace(/"/g, '\\"')}",
            column_type: ${ct.apiType}
          ) {
            id
            title
            type
          }
        }
      `);

      const created = res?.data?.create_column;
      if (!created?.id) {
        throw new Error(
          res?.errors?.[0]?.message || 'Column creation failed — no ID returned.'
        );
      }

      onColumnCreated({ id: created.id, title: created.title, type: created.type });
      setTitles({});
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to create column. Please try again.');
    } finally {
      setLoadingType(null);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className='ccm-overlay'
      role='dialog'
      aria-modal='true'
      aria-label='Column Center'
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div className='ccm-panel'>
        {/* Header */}
        <div className='ccm-header'>
          <div className='ccm-header-left'>
            <span className='ccm-title'>Column Center</span>
            {fieldLabel && (
              <span className='ccm-field-context'>
                Adding column for: <strong>{fieldLabel}</strong>
              </span>
            )}
          </div>
          <button
            className='ccm-close-btn'
            onClick={onClose}
            aria-label='Close modal'
          >
            <Icon icon={Close} iconSize={18} />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className='ccm-error-banner' role='alert'>
            {error}
          </div>
        )}

        {/* Recipient Email warning */}
        {fieldLabel === 'Recipient Email' && (
          <div className='ccm-error-banner' style={{ backgroundColor: '#fffbeb', color: '#92400e', borderColor: '#fde68a' }}>
            ⚠️ Each cell should contain only a single email address — multiple emails per cell are not supported.
          </div>
        )}

        {/* Column type list — Design C: top info zone + bottom action strip */}
        <div className='ccm-list'>
          {visibleTypes.length === 0 ? (
            <p className='ccm-empty'>No supported column types available.</p>
          ) : (
            visibleTypes.map((ct) => {
              const isCreating = loadingType === ct.apiType;
              return (
                <div key={ct.apiType} className={`ccm-card ${isCreating ? 'ccm-card--loading' : ''}`}>
                  {/* Top: icon + label + description */}
                  <div className='ccm-card-top'>
                    <div className='ccm-card-icon'>
                      <Icon icon={ct.icon} iconSize={20} />
                    </div>
                    <div className='ccm-card-info'>
                      <span className='ccm-card-label'>{ct.label}</span>
                      <span className='ccm-card-desc'>{ct.description}</span>
                    </div>
                  </div>
                  
                  {/* Bottom: input + button on same row */}
                  <div className='ccm-card-bottom'>
                    <input
                      className='ccm-card-title-input'
                      type='text'
                      value={getTitle(ct)}
                      onChange={(e) => handleTitleChange(ct.apiType, e.target.value)}
                      placeholder="Input column name here"
                      aria-label={`Column title for ${ct.label}`}
                      disabled={isCreating}
                      maxLength={100}
                    />
                    <button
                      className='ccm-card-btn'
                      onClick={() => handleCreate(ct)}
                      disabled={!!loadingType}
                      aria-label={`Add ${ct.label} column to board`}
                    >
                      {isCreating ? (
                        <span className='ccm-spinner' aria-label='Creating…' />
                      ) : (
                        'Add to board'
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className='ccm-footer'>
          <button className='ccm-cancel-btn' onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateColumnModal;
